import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { kb } from "@/lib/api";

const BLOCKS = [
  { key: "block_a", label: "A · Corporate Identity & SAM.gov", short: "Corporate Identity",
    sub: "Entity & SAM.gov", desc: "Legal entity, registration, business types and set-asides used across every proposal." },
  { key: "block_b", label: "B · Personnel & Roles", short: "Personnel & Roles",
    sub: "Team & labor categories", desc: "Headcount, labor categories, and key personnel with their experience and clearances." },
  { key: "block_c", label: "C · Credentials & Compliance", short: "Credentials & Compliance",
    sub: "Frameworks & clearances", desc: "Compliance frameworks held and facility/individual clearance holdings." },
  { key: "block_d", label: "D · Education & Military", short: "Education & Military",
    sub: "Degrees & veterans", desc: "Education profile and military / veteran service for set-aside qualification." },
];

function ChipMulti({ options, value = [], onChange }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div className="onb-chips">
      {options.map((o) => (
        <span key={o} onClick={() => toggle(o)} className={`onb-chip ${value.includes(o) ? "sel" : ""}`}>{o}</span>
      ))}
    </div>
  );
}

function Field({ label, full, children }) {
  return <div className={`onb-field ${full ? "full" : ""}`}><label>{label}</label>{children}</div>;
}

export default function Onboarding() {
  const nav = useNavigate();
  const [tax, setTax] = useState({});
  const [step, setStep] = useState(0);
  const [a, setA] = useState({ poc: {} });
  const [b, setB] = useState({ personnel: [], labor_categories: [] });
  const [c, setC] = useState({ compliance_frameworks: [], clearances: [] });
  const [d, setD] = useState({ education: [], military: [] });
  const [completeness, setCompleteness] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    (async () => {
      const [t, p] = await Promise.all([kb.taxonomy().catch(() => ({})), kb.companyProfile()]);
      setTax(t);
      setA({ poc: {}, ...(p.block_a || {}) });
      setB({ personnel: [], labor_categories: [], ...(p.block_b || {}) });
      setC({ compliance_frameworks: [], clearances: [], ...(p.block_c || {}) });
      setD({ education: [], military: [], ...(p.block_d || {}) });
      setCompleteness(p.completeness || {});
    })();
  }, []);

  const dataFor = { block_a: a, block_b: b, block_c: c, block_d: d };

  const saveBlock = async (key) => {
    setBusy(true);
    try {
      const res = await kb.saveProfileBlock(key, dataFor[key]);
      setCompleteness(res.completeness || {});
      setSaved(key);
      setTimeout(() => setSaved(""), 1500);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Save failed (KB Manager / PM role required)");
    } finally { setBusy(false); }
  };

  const next = async () => {
    if (step < BLOCKS.length) await saveBlock(BLOCKS[step].key);
    setStep(Math.min(step + 1, BLOCKS.length));
  };

  const prefillFromExcel = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const preview = await kb.importPreview(file);
      const r = await kb.importConfirm({ skills: preview.skills, certifications: preview.certifications });
      window.alert(`Imported ${r.skills_added} skills and ${r.certifications_added} certifications into the knowledge base.`);
    } catch (e) { window.alert(e?.response?.data?.detail || "Could not import workbook"); }
    finally { setBusy(false); }
  };

  const addRow = (obj, set, key, blank) => set({ ...obj, [key]: [...(obj[key] || []), blank] });
  const setRow = (obj, set, key, i, patch) =>
    set({ ...obj, [key]: obj[key].map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const delRow = (obj, set, key, i) => set({ ...obj, [key]: obj[key].filter((_, j) => j !== i) });

  const overall = Math.round(
    (BLOCKS.reduce((s, bl) => s + (completeness[bl.key] || 0), 0) / BLOCKS.length) * 100
  );

  return (
    <div className="page onboarding" data-testid="page-onboarding">
      <div className="page-header">
        <div>
          <div className="page-title">Company Onboarding</div>
          <div className="page-subtitle">Four-block capability profile · taxonomy-guided · {overall}% complete</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => nav("/skills")}>← Back to Skills</button>
      </div>

      <div className="onb-grid">
        {/* Left rail — numbered stepper */}
        <div className="onb-stepper">
          {BLOCKS.map((bl, i) => {
            const pct = Math.round((completeness[bl.key] || 0) * 100);
            const done = pct > 0 && i !== step;
            return (
              <div key={bl.key} className={`onb-step ${i === step ? "active" : ""} ${done ? "done" : ""}`}
                onClick={() => setStep(i)} data-testid={`step-${bl.key}`}>
                <div className="onb-step-num">{done ? "✓" : i + 1}</div>
                <div>
                  <div className="onb-step-label">{bl.short}</div>
                  <div className="onb-step-sub">{bl.sub} · {pct}%</div>
                </div>
              </div>
            );
          })}
          <div className={`onb-step ${step === BLOCKS.length ? "active" : ""}`}
            onClick={() => setStep(BLOCKS.length)} data-testid="step-review">
            <div className="onb-step-num">★</div>
            <div>
              <div className="onb-step-label">Review</div>
              <div className="onb-step-sub">Finish &amp; summary</div>
            </div>
          </div>
        </div>

        {/* Right — active block */}
        <div className="card" style={{ padding: "22px 26px" }}>
          {step < BLOCKS.length && (
            <div className="onb-block-head">
              <div>
                <div className="onb-block-title">{BLOCKS[step].label}</div>
                <div className="onb-block-desc">{BLOCKS[step].desc}</div>
              </div>
              {step === 0 && (
                <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  ⬆ Prefill from Excel
                  <input type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => prefillFromExcel(e.target.files[0])} data-testid="wizard-import" />
                </label>
              )}
            </div>
          )}

          {step === 0 && (
            <>
              <div className="onb-fields">
                <Field label="Legal entity name"><input value={a.legal_name || ""} onChange={(e) => setA({ ...a, legal_name: e.target.value })} data-testid="a-legal" placeholder="Acme Federal Solutions LLC" /></Field>
                <Field label="DBA (if any)"><input value={a.dba || ""} onChange={(e) => setA({ ...a, dba: e.target.value })} /></Field>
                <Field label="UEI"><input value={a.uei || ""} onChange={(e) => setA({ ...a, uei: e.target.value })} placeholder="12 characters" /></Field>
                <Field label="CAGE code"><input value={a.cage || ""} onChange={(e) => setA({ ...a, cage: e.target.value })} /></Field>
                <Field label="SAM.gov status"><select value={a.sam_status || "Active"} onChange={(e) => setA({ ...a, sam_status: e.target.value })}><option>Active</option><option>Pending</option><option>Expired</option><option>Not Registered</option></select></Field>
                <Field label="SAM.gov expiry"><input value={a.sam_expiry || ""} onChange={(e) => setA({ ...a, sam_expiry: e.target.value })} placeholder="2027-01-01" /></Field>
              </div>
              <div className="onb-section-label">Business types</div>
              <ChipMulti options={tax.business_types || []} value={a.business_types || []} onChange={(v) => setA({ ...a, business_types: v })} />
              <div className="onb-section-label">Set-asides</div>
              <ChipMulti options={tax.set_asides || []} value={a.set_asides || []} onChange={(v) => setA({ ...a, set_asides: v })} />
              <div className="onb-fields" style={{ marginTop: 14 }}>
                <Field label="NAICS codes (comma-separated)" full><input value={(a.naics || []).join(", ")} onChange={(e) => setA({ ...a, naics: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="541512, 541519" /></Field>
                <Field label="POC name"><input value={a.poc?.name || ""} onChange={(e) => setA({ ...a, poc: { ...a.poc, name: e.target.value } })} /></Field>
                <Field label="POC email"><input value={a.poc?.email || ""} onChange={(e) => setA({ ...a, poc: { ...a.poc, email: e.target.value } })} /></Field>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="onb-fields">
                <Field label="Total headcount"><input type="number" min="0" value={b.total_headcount || ""} onChange={(e) => setB({ ...b, total_headcount: Number(e.target.value) || 0 })} data-testid="b-headcount" /></Field>
              </div>
              <div className="onb-section-label">Labor categories</div>
              <div className="onb-rows">
                {(b.labor_categories || []).map((r, i) => (
                  <div key={i} className="onb-row">
                    <select style={{ flex: 2 }} value={r.title || ""} onChange={(e) => setRow(b, setB, "labor_categories", i, { title: e.target.value })}>
                      <option value="">Select labor category…</option>{(tax.labor_categories || []).map((l) => <option key={l}>{l}</option>)}
                    </select>
                    <input style={{ width: 90 }} type="number" placeholder="Count" value={r.count || ""} onChange={(e) => setRow(b, setB, "labor_categories", i, { count: Number(e.target.value) || 0 })} />
                    <input style={{ width: 100 }} type="number" placeholder="Avg yrs" value={r.avg_years || ""} onChange={(e) => setRow(b, setB, "labor_categories", i, { avg_years: Number(e.target.value) || 0 })} />
                    <button className="onb-x" title="Remove" onClick={() => delRow(b, setB, "labor_categories", i)}>×</button>
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => addRow(b, setB, "labor_categories", { title: "", count: 0, avg_years: 0 })}>+ Add labor category</button>
              <div className="onb-section-label">Key personnel</div>
              <div className="onb-rows">
                {(b.personnel || []).map((r, i) => (
                  <div key={i} className="onb-row">
                    <input style={{ flex: 1 }} placeholder="Name" value={r.name || ""} onChange={(e) => setRow(b, setB, "personnel", i, { name: e.target.value })} />
                    <input style={{ flex: 1 }} placeholder="Role" value={r.role || ""} onChange={(e) => setRow(b, setB, "personnel", i, { role: e.target.value })} />
                    <input style={{ width: 80 }} type="number" placeholder="Yrs" value={r.years_experience || ""} onChange={(e) => setRow(b, setB, "personnel", i, { years_experience: Number(e.target.value) || 0 })} />
                    <select style={{ width: 130 }} value={r.clearance || ""} onChange={(e) => setRow(b, setB, "personnel", i, { clearance: e.target.value })}><option value="">Clearance…</option>{(tax.clearance_levels || []).map((cl) => <option key={cl}>{cl}</option>)}</select>
                    <button className="onb-x" title="Remove" onClick={() => delRow(b, setB, "personnel", i)}>×</button>
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => addRow(b, setB, "personnel", { name: "", role: "", years_experience: 0, clearance: "" })}>+ Add person</button>
            </>
          )}

          {step === 2 && (
            <>
              <div className="onb-section-label">Compliance frameworks</div>
              <ChipMulti options={tax.compliance_frameworks || []} value={c.compliance_frameworks || []} onChange={(v) => setC({ ...c, compliance_frameworks: v })} />
              <div className="onb-section-label">Clearance holdings</div>
              <div className="onb-rows">
                {(c.clearances || []).map((r, i) => (
                  <div key={i} className="onb-row">
                    <select style={{ flex: 1 }} value={r.level || ""} onChange={(e) => setRow(c, setC, "clearances", i, { level: e.target.value })}><option value="">Level…</option>{(tax.clearance_levels || []).map((cl) => <option key={cl}>{cl}</option>)}</select>
                    <input style={{ width: 100 }} type="number" placeholder="Count" value={r.count || ""} onChange={(e) => setRow(c, setC, "clearances", i, { count: Number(e.target.value) || 0 })} />
                    <button className="onb-x" title="Remove" onClick={() => delRow(c, setC, "clearances", i)}>×</button>
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => addRow(c, setC, "clearances", { level: "", count: 0 })}>+ Add clearance level</button>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 16 }}>
                Individual certifications are managed on the Skills &amp; Certifications page.
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="onb-section-label">Education</div>
              <div className="onb-rows">
                {(d.education || []).map((r, i) => (
                  <div key={i} className="onb-row">
                    <select style={{ width: 160 }} value={r.degree || ""} onChange={(e) => setRow(d, setD, "education", i, { degree: e.target.value })}><option value="">Degree…</option>{(tax.degrees || []).map((dg) => <option key={dg}>{dg}</option>)}</select>
                    <input style={{ flex: 1 }} placeholder="Field of study" value={r.field || ""} onChange={(e) => setRow(d, setD, "education", i, { field: e.target.value })} />
                    <input style={{ width: 90 }} type="number" placeholder="Count" value={r.count || ""} onChange={(e) => setRow(d, setD, "education", i, { count: Number(e.target.value) || 0 })} />
                    <button className="onb-x" title="Remove" onClick={() => delRow(d, setD, "education", i)}>×</button>
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => addRow(d, setD, "education", { degree: "", field: "", count: 0 })}>+ Add education</button>
              <div className="onb-section-label">Veteran-owned business</div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 400, fontSize: 13 }}>
                <input type="checkbox" checked={!!d.veteran_owned} onChange={(e) => setD({ ...d, veteran_owned: e.target.checked })} style={{ width: "auto" }} /> Yes, this is a veteran-owned business
              </label>
              <div className="onb-section-label">Military / veteran profiles</div>
              <div className="onb-rows">
                {(d.military || []).map((r, i) => (
                  <div key={i} className="onb-row">
                    <select style={{ flex: 1 }} value={r.branch || ""} onChange={(e) => setRow(d, setD, "military", i, { branch: e.target.value })}><option value="">Branch…</option>{(tax.military_branches || []).map((m) => <option key={m}>{m}</option>)}</select>
                    <select style={{ flex: 1 }} value={r.status || ""} onChange={(e) => setRow(d, setD, "military", i, { status: e.target.value })}><option value="">Status…</option>{(tax.military_status || []).map((m) => <option key={m}>{m}</option>)}</select>
                    <input style={{ width: 90 }} type="number" placeholder="Count" value={r.count || ""} onChange={(e) => setRow(d, setD, "military", i, { count: Number(e.target.value) || 0 })} />
                    <button className="onb-x" title="Remove" onClick={() => delRow(d, setD, "military", i)}>×</button>
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => addRow(d, setD, "military", { branch: "", status: "", count: 0 })}>+ Add profile</button>
            </>
          )}

          {step === BLOCKS.length && (
            <>
              <div className="onb-block-head">
                <div>
                  <div className="onb-block-title">Review · Profile completeness</div>
                  <div className="onb-block-desc">Your four-block capability profile powers AI matching and proposal generation.</div>
                </div>
              </div>
              <div className="onb-review">
                {BLOCKS.map((bl) => {
                  const pct = Math.round((completeness[bl.key] || 0) * 100);
                  return (
                    <div key={bl.key} className="onb-ring">
                      <div className="onb-ring-pct" style={{ color: pct >= 70 ? "var(--success)" : pct > 0 ? "var(--warning)" : "var(--text-muted)" }}>{pct}%</div>
                      <div className="onb-ring-label">{bl.short}</div>
                    </div>
                  );
                })}
              </div>
              <button className="btn btn-primary" onClick={() => nav("/skills")} data-testid="finish-btn">Finish</button>
            </>
          )}

          {/* Footer nav */}
          {step < BLOCKS.length && (
            <div className="onb-footer">
              <button className="btn btn-ghost btn-sm" disabled={step === 0} onClick={() => setStep(step - 1)}>← Previous</button>
              <span className="onb-saved">{saved === BLOCKS[step].key ? "✓ Saved" : busy ? "Saving…" : ""}</span>
              <div className="flex gap-8">
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => saveBlock(BLOCKS[step].key)}>Save</button>
                <button className="btn btn-primary btn-sm" disabled={busy} onClick={next} data-testid="next-btn">Save &amp; Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
